import React, { useMemo } from 'react';
import styled from 'styled-components';
import Moment from 'react-moment';

import { getMediaUrl } from '../../helpers';
import { groupMatchesByCompetitionRound } from '../../helpers/matchGrouping';
import { resultColorFor } from '../../helpers/matchResult';
import MatchCard from '../MatchCard';
import { useLexicon } from '../../hooks/useLexicon';

const scoreText = (m) => {
  if (typeof m.score === 'string' && m.score.length > 0) return m.score;
  const home = m.home_team_score;
  const away = m.away_team_score;
  if (home == null || away == null) return null;
  return `${home}:${away}`;
};

const PlayerMatchesList = ({ matches = [], playerTeams = [] }) => {
  const l = useLexicon();
  const rows = useMemo(() => groupMatchesByCompetitionRound(matches), [matches]);

  if (rows.length === 0) return null;

  return (
    <MatchList>
      {rows.map(({ match: m, label, showSubheader }, idx) => (
        <React.Fragment key={m.id}>
          {showSubheader && (
            <CompetitionStrip $first={idx === 0}>
              {m.competition_logo && (
                <CompLogo src={getMediaUrl(m.competition_logo)} alt="" />
              )}
              <CompName>{label}</CompName>
            </CompetitionStrip>
          )}
          <MatchCard
            to={`/player/matches/${m.id}`}
            homeTeam={m.home_team}
            homeTeamLogo={m.home_team_logo}
            awayTeam={m.away_team}
            awayTeamLogo={m.away_team_logo}
            score={scoreText(m)}
            scoreColor={resultColorFor({
              homeTeam: m.home_team,
              homeTeamScore: m.home_team_score,
              awayTeamScore: m.away_team_score,
              score: m.score,
              playerIsHome: m.player_is_home,
              playerTeams,
            })}
            meta={[
              <Moment format="DD.MM.YYYY">{m.date}</Moment>,
              Boolean(m.mins_played) ? `${m.mins_played} ${l(1702)}` : l(1703),
            ]}
          />
        </React.Fragment>
      ))}
    </MatchList>
  );
};

export default PlayerMatchesList;

const MatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Competition heading sits OUTSIDE the cards — small uppercase strip
// that groups matches without competing with the card itself for attention.
const CompetitionStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${(props) => (props.$first ? '0 4px 4px' : '12px 4px 4px')};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
`;

const CompLogo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  opacity: 0.8;
  flex-shrink: 0;
`;

const CompName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
